import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';

export default function StoreMerchandiseOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [form, setForm] = useState({
    orderStatus: 'placed',
    paymentStatus: 'paid',
    trackingNumber: '',
    courierPartner: 'Delhivery',
    notes: ''
  });

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.get('/api/v1/admin/store/orders?orderType=merchandise', { headers });
      if (res.data.success) setOrders(res.data.data);
    } catch (err) {
      setError('Failed to load merchandise orders.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleOpenEdit = (order) => {
    setSelectedOrder(order);
    setForm({
      orderStatus: order.orderStatus || 'placed',
      paymentStatus: order.paymentStatus || 'pending',
      trackingNumber: order.trackingNumber || '',
      courierPartner: order.courierPartner || 'Delhivery',
      notes: order.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSaveStatus = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.patch(`/api/v1/admin/store/orders/${selectedOrder.id}/status`, form, { headers });
      setDialogOpen(false);
      fetchOrders();
    } catch (err) {
      alert('Failed to update order status');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <LocalShippingIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">Merchandise Orders & Dispatch</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage public merchandise orders, home delivery shipments, and office pickups.
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead sx={{ bgcolor: '#F8FAFC' }}>
              <TableRow>
                <TableCell>Order Ref ID</TableCell>
                <TableCell>Customer Details</TableCell>
                <TableCell>Fulfillment</TableCell>
                <TableCell>Shipping Address / Pickup</TableCell>
                <TableCell>Purchased Items</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Dispatch Status</TableCell>
                <TableCell>Courier / Tracking</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">No merchandise orders recorded yet.</TableCell>
                </TableRow>
              ) : (
                orders.map((o) => (
                  <TableRow key={o.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">{o.orderNumber}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(o.createdAt).toLocaleDateString('en-IN')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{o.customerName}</Typography>
                      <Typography variant="caption" color="text.secondary">{o.customerPhone}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={o.fulfillmentType === 'home_delivery' ? <HomeIcon /> : <BusinessIcon />}
                        label={o.fulfillmentType === 'home_delivery' ? 'Home Delivery' : 'Office Pickup'}
                        size="small"
                        color={o.fulfillmentType === 'home_delivery' ? 'primary' : 'secondary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      {o.fulfillmentType === 'home_delivery' && o.shippingAddress ? (
                        <Typography variant="caption" display="block">
                          {o.shippingAddress.line1}, {o.shippingAddress.city} - {o.shippingAddress.pincode}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {o.pickupLocation || 'Invincible Head Office, Ahmedabad'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {o.items?.map((it) => (
                        <Typography key={it.id} variant="caption" display="block">
                          • {it.productName} {it.variantName ? `(${it.variantName})` : ''} x {it.quantity}
                        </Typography>
                      ))}
                    </TableCell>
                    <TableCell>
                      <strong>₹{Number(o.grandTotal).toLocaleString('en-IN')}</strong>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          o.paymentStatus === 'pending_office_payment'
                            ? 'PAY AT OFFICE (PENDING)'
                            : o.paymentStatus.toUpperCase()
                        }
                        size="small"
                        color={
                          o.paymentStatus === 'paid'
                            ? 'success'
                            : o.paymentStatus === 'pending_office_payment'
                            ? 'info'
                            : 'warning'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={o.orderStatus.replace('_', ' ').toUpperCase()}
                        size="small"
                        color={o.orderStatus === 'delivered' || o.orderStatus === 'completed' ? 'success' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>
                      {o.trackingNumber ? (
                        <Typography variant="caption" fontWeight="bold" color="primary">
                          {o.courierPartner}: {o.trackingNumber}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">Not Assigned</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(o)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Order Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Merchandise Order: {selectedOrder?.orderNumber}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              select
              label="Fulfillment / Dispatch Status"
              fullWidth
              value={form.orderStatus}
              onChange={(e) => setForm({ ...form, orderStatus: e.target.value })}
            >
              <MenuItem value="placed">Placed / New</MenuItem>
              <MenuItem value="processing">Processing & Packing</MenuItem>
              <MenuItem value="ready_for_pickup">Ready for Office Pickup</MenuItem>
              <MenuItem value="shipped">Dispatched / In Transit</MenuItem>
              <MenuItem value="delivered">Delivered / Handed Over</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>

            <TextField
              select
              label="Payment Status"
              fullWidth
              value={form.paymentStatus}
              onChange={(e) => setForm({ ...form, paymentStatus: e.target.value })}
            >
              <MenuItem value="pending">Pending Online Payment</MenuItem>
              <MenuItem value="pending_office_payment">Pending Payment at Office</MenuItem>
              <MenuItem value="paid">Paid & Confirmed</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
            </TextField>

            <TextField
              label="Courier Partner"
              fullWidth
              value={form.courierPartner}
              onChange={(e) => setForm({ ...form, courierPartner: e.target.value })}
              placeholder="e.g. Delhivery, DTDC, BlueDart"
            />

            <TextField
              label="Tracking / AWB Number"
              fullWidth
              value={form.trackingNumber}
              onChange={(e) => setForm({ ...form, trackingNumber: e.target.value })}
              placeholder="e.g. 1234567890"
            />

            <TextField
              label="Admin Internal Notes"
              fullWidth
              multiline
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSaveStatus}>Save Order</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
